import { useTranslation } from 'react-i18next';
import { Box, Select, MenuItem, FormControl, Typography } from '@mui/material';
import {
  FALLBACK_LANGUAGE,
  formatLanguageLabel,
  Language,
  getAvailableLanguages,
} from '@/components/common/translateFunctions';
import { provideFlagIcon } from '@/components/common/IconProvider';
import { LANGUAGE_LOCAL_STORAGE_KEY } from '@/utilities/language';
import { BaseSettingsPaper, settingsStartIconSx } from './SettingsComponents.style';

export default function LanguageSelect() {
  const { i18n } = useTranslation();

  const availableLanguages: Language[] = getAvailableLanguages();
  const isMobileFormat = false;
  const selected = availableLanguages.find((l) => l.code === i18n.language) ?? FALLBACK_LANGUAGE;

  async function languageChange(language: Language) {
    await i18n.changeLanguage(language.code);
    localStorage.setItem(LANGUAGE_LOCAL_STORAGE_KEY, language.code);
  }

  return (
    <FormControl fullWidth data-qa-element-id="language_selector">
      <BaseSettingsPaper data-qa-element-id="language_selector_paper">
        <Select
          displayEmpty
          value={selected?.code ?? ''}
          MenuProps={{
            PaperProps: {
              sx: {
                maxHeight: '40vh',
                overflowY: 'auto',
              },
            },
          }}
          sx={{
            width: '100%',
            '& fieldset': {
              border: 'none',
            },
          }}
          renderValue={() => (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.25rem',
              }}
            >
              <Box component="img" src={provideFlagIcon(selected.code)} sx={settingsStartIconSx} />
              <Typography data-qa-element-id="selected_language_label">
                {formatLanguageLabel(selected, isMobileFormat)}
              </Typography>
            </Box>
          )}
        >
          {availableLanguages.map((language) => (
            <MenuItem key={language.code} value={language.code} onClick={() => languageChange(language)}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.25rem',
                }}
              >
                <Box component="img" src={provideFlagIcon(language.code)} sx={settingsStartIconSx} />
                <Typography data-qa-element-id={`language_for_${language.name}`}>
                  {formatLanguageLabel(language, isMobileFormat)}
                </Typography>
              </Box>
            </MenuItem>
          ))}
        </Select>
      </BaseSettingsPaper>
    </FormControl>
  );
}
